import { addDays, formatCalendarDate, isValidCalendarDate } from '@lib/datetime'
import { Trip } from '@types'

/**
 * The Day header (CONTEXT.md): the two lines that name a trip day, built the
 * same way as the iOS `TripDayTitle` / `ItineraryDayPresentation` pair so
 * every platform names a day identically. Pure so the cases are unit-testable.
 */

/** One destination's slice of the trip, reduced to what the header needs. */
export interface DestinationSegment {
	name: string
	startDayIndex: number
	dayCount: number
}

export interface DayHeader {
	/** The day's own name, else its date ("Sat, Oct 3"), else "Day 3" on an undated trip. */
	title: string
	/** Whatever the title has not said yet; null when nothing is left to say. */
	subtitle: string | null
	/** The counter-and-destination phrase on its own — "Day 2 in Tokyo" — for copy that names the day; null when there is none. */
	place: string | null
}

export interface DayHeaderInput {
	/** Zero-based trip day. */
	dayIndex: number
	/** Compact or dashed calendar date; null / '' on an undated trip. */
	startDate: string | null | undefined
	segments: DestinationSegment[]
	dayName: string | null | undefined
}

const trimmed = (value: string | null | undefined): string | null => {
	const text = value?.trim() ?? ''
	return text.length ? text : null
}

/** A run of trip days: a header segment, or the trip destination it was built from. */
export interface DaySpan {
	startDayIndex: number
	dayCount: number
}

const coversDay = (span: DaySpan, dayIndex: number): boolean =>
	dayIndex >= span.startDayIndex && dayIndex < span.startDayIndex + span.dayCount

/** Where the span covering a zero-based day sits in trip order, or -1 when none does. */
export const spanIndexForDay = (spans: DaySpan[], dayIndex: number): number =>
	spans.findIndex(span => coversDay(span, dayIndex))

/** The span covering a zero-based day — whatever was handed in, so a destination comes back whole — or null when none does. */
export const segmentForDay = <S extends DaySpan>(spans: S[], dayIndex: number): S | null =>
	spans.find(span => coversDay(span, dayIndex)) ?? null

/** How a destination is named everywhere: its location's primary text, falling back to the full text. */
export const destinationName = (location: { primaryText: string | null; fullText: string }): string =>
	(location.primaryText ?? location.fullText).trim()

/** The trip's destinations as header segments. */
export const destinationSegments = (trip: Pick<Trip, 'destinations'>): DestinationSegment[] =>
	trip.destinations.map(d => ({
		name: destinationName(d.location),
		startDayIndex: d.startDayIndex,
		dayCount: d.dayCount,
	}))

/** How a day leads when it has no name: its date ("Sat, Oct 3"), or "Day 3" on an undated trip. */
export const dayLead = (dayIndex: number, startDate: string | null | undefined): string =>
	isValidCalendarDate(startDate) ? formatCalendarDate(addDays(startDate, dayIndex), 'weekday') : `Day ${dayIndex + 1}`

export const dayHeader = ({ dayIndex, startDate, segments, dayName }: DayHeaderInput): DayHeader => {
	const dated = isValidCalendarDate(startDate)
	const lead = dayLead(dayIndex, startDate)
	const segment = segmentForDay(segments, dayIndex)
	const destination = trimmed(segment?.name)

	// The counter belongs to whatever the line names: the day within its
	// destination, or the trip day when no destination owns it. An undated
	// trip's lead already reads "Day 3", so the counter is never printed twice.
	const counter = !dated
		? null
		: segment
			? `Day ${dayIndex - segment.startDayIndex + 1}`
			: `Day ${dayIndex + 1}`

	const place =
		counter && destination
			? `${counter} in ${destination}`
			: (counter ?? destination ?? '')

	const name = trimmed(dayName)
	const pieces = (name === null ? [place] : [lead, place]).filter(p => p.length > 0)
	const subtitle = pieces.join(' · ')

	return { title: name ?? lead, subtitle: subtitle.length ? subtitle : null, place: place.length ? place : null }
}

/**
 * The day as one line where its date is already on screen (under the
 * create-event calendar): the header's name and place without the date —
 * "Beach Day · Day 1 in Toronto", or "Day 1 in Toronto" for an unnamed day.
 * null when there is nothing but the date to say.
 */
export const dayLine = (input: DayHeaderInput): string | null => {
	const line = [trimmed(input.dayName), dayHeader(input).place].filter(Boolean).join(' · ')
	return line.length ? line : null
}
